import { TFunction } from 'i18next';

import { DocumentType } from 'support/types';
import { TransactionData, TransactionGraphData } from 'support/types/dtos';

import {
  ChartDetails,
  DateRange,
  StatChangeType,
  TransactionSeries,
  TransactionVolumeStats,
  VolumeStats,
} from './types';

export const convertToSeries = (
  chartDetails: ChartDetails,
  chartDateParam: number,
  allDates: Array<number>,
): TransactionSeries => {
  if (!chartDetails.data.length) {
    return { name: chartDetails.name, data: [] };
  }
  const seriesData: Array<Array<number>> = [];

  allDates.forEach((date) => {
    if (date > chartDateParam) {
      seriesData.push([date, 0]);
    }
  });

  chartDetails.data.forEach((dataPoint: TransactionData) => {
    const dateTime = new Date(dataPoint.date).getTime();

    if (dateTime > chartDateParam) {
      const dateIndex = seriesData.findIndex((seriesPoint: Array<number>) => seriesPoint[0] === dateTime);
      if (dateIndex >= 0) {
        seriesData[dateIndex] = [dateTime, dataPoint.count];
      } else {
        seriesData.push([dateTime, dataPoint.count]);
      }
    }
  });

  return {
    name: chartDetails.name,
    data: seriesData.sort((a: Array<number>, b: Array<number>) => (a[0] < b[0] ? -1 : 1)),
  };
};

export const getAllDates = (dateRangeOptions: Array<DateRange>) => {
  if (!dateRangeOptions.length) {
    return [];
  }

  const dates: Array<number> = [];
  let startDate = new Date(Date.now()).setUTCHours(0, 0, 0, 0);
  dates.push(startDate);

  while (startDate > dateRangeOptions[dateRangeOptions.length - 1].value) {
    startDate -= 1000 * 60 * 60 * 24;
    dates.push(startDate);
  }

  return dates;
};

export const sumTransactionCount = (
  transactionsData: Array<TransactionData> | undefined,
  volumeDateRange: number,
  previousVolumeDateRange?: number,
) => {
  if (!transactionsData) {
    return 0;
  }

  let total = 0;

  transactionsData.forEach((dataPoint: TransactionData) => {
    const date = new Date(dataPoint.date).getTime();
    const count = dataPoint.count;

    if (previousVolumeDateRange) {
      if (date < volumeDateRange && date > previousVolumeDateRange) {
        total += count;
      }
    } else if (date > volumeDateRange) {
      total += count;
    }
  });

  return total;
};

export const changePercentage = (original: number, current: number) => {
  if ((!original && !current) || original === current) {
    return 0;
  }
  if (!original && current) {
    return 100;
  }
  return Math.round(((current - original) / original) * 100);
};

export const changeType = (stat: TransactionVolumeStats) => {
  if (!stat.change) {
    return StatChangeType.level;
  } else if (stat.change > 0) {
    return StatChangeType.increase;
  } else {
    return StatChangeType.decrease;
  }
};

const VOLUME_SERIES: Array<{ key: keyof TransactionGraphData; type: DocumentType; nameKey: string }> = [
  { key: 'orders', type: DocumentType.order, nameKey: 'dashboard:transactions.orders' },
  { key: 'shippingNotices', type: DocumentType.shippingNotice, nameKey: 'dashboard:transactions.shippingNotices' },
  { key: 'invoices', type: DocumentType.invoice, nameKey: 'dashboard:transactions.invoices' },
  {
    key: 'dispatchInstructions',
    type: DocumentType.dispatchInstruction,
    nameKey: 'dashboard:transactions.dispatchInstructions',
  },
  {
    key: 'dispatchInstructionResponses',
    type: DocumentType.dispatchResponse,
    nameKey: 'dashboard:transactions.dispatchInstructionResponses',
  },
];

export const getVolumeStats = (
  transactions: TransactionGraphData | undefined,
  volumeDateRange: number,
  previousVolumeDateRange: number,
  t: TFunction,
): VolumeStats => {
  const volumeStats: VolumeStats = {
    allTransactions: {
      name: t('dashboard:transactions.allTransactions'),
      count: sumAllTransactionsTypes(transactions, volumeDateRange),
      previousCount: sumAllTransactionsTypes(transactions, volumeDateRange, volumeDateRange - previousVolumeDateRange),
      type: undefined,
    },
  };

  // Only include a series when the response carries its key, so the dashboard
  // never shows a count for a message type the partner does not have.
  VOLUME_SERIES.forEach(({ key, type, nameKey }) => {
    const data = transactions?.[key];
    if (data === undefined) {
      return;
    }

    volumeStats[key] = {
      type,
      name: t(nameKey),
      count: sumTransactionCount(data, volumeDateRange),
      previousCount: sumTransactionCount(data, volumeDateRange, volumeDateRange - previousVolumeDateRange),
    };
  });

  (Object.keys(volumeStats) as Array<keyof VolumeStats>).forEach((type) => {
    const stats = volumeStats[type];
    if (!stats) {
      return;
    }

    stats.change = changePercentage(stats.previousCount, stats.count);
    stats.changeType = changeType(stats);
  });

  return volumeStats;
};

/**
 * i18n keys
 * t('dashboard:transactions.orders')
 * t('dashboard:transactions.shippingNotices')
 * t('dashboard:transactions.invoices')
 * t('dashboard:transactions.dispatchInstructions')
 * t('dashboard:transactions.dispatchInstructionResponses')
 */

export const TRANSACTION_TYPE_OPTIONS: Array<{ label: string; value: DocumentType }> = [
  {
    label: 'common:messageTypes.plural.ORDER',
    value: DocumentType.order,
  },
  {
    label: 'common:messageTypes.plural.SHIPPING_NOTICE',
    value: DocumentType.shippingNotice,
  },
  {
    label: 'common:messageTypes.plural.INVOICE',
    value: DocumentType.invoice,
  },
  {
    label: 'common:messageTypes.plural.DISPATCH_INSTRUCTION',
    value: DocumentType.dispatchInstruction,
  },
  {
    label: 'common:messageTypes.plural.DISPATCH_INSTRUCTION_RESPONSE',
    value: DocumentType.dispatchResponse,
  },
];

const sumAllTransactionsTypes = (
  transactions: TransactionGraphData | undefined,
  volumeDateRange: number,
  previousVolumeDateRange?: number,
) => {
  return (
    sumTransactionCount(transactions?.orders, volumeDateRange, previousVolumeDateRange) +
    sumTransactionCount(transactions?.shippingNotices, volumeDateRange, previousVolumeDateRange) +
    sumTransactionCount(transactions?.invoices, volumeDateRange, previousVolumeDateRange) +
    sumTransactionCount(transactions?.dispatchInstructions, volumeDateRange, previousVolumeDateRange) +
    sumTransactionCount(transactions?.dispatchInstructionResponses, volumeDateRange, previousVolumeDateRange)
  );
};
